/**
 * Level 3 of a search result: the overlay preview pane
 * (search-result-disclosure 03).
 *
 * Right drawer on tablet/desktop, bottom sheet on phones, always over the
 * results so closing it returns to the still-expanded row. It shows the whole
 * picture (`contain`), every photo in a filmstrip, the description, chips,
 * deadlines and "what's inside", plus the direct open action.
 */

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CloseIcon } from "../../shared/components/icons";
import {
  CONTAINER_DEADLINE_VOCABULARY,
  DeadlineChips,
  ITEM_DEADLINE_VOCABULARY,
} from "../../shared/components/Deadlines";
import { useT } from "../../shared/i18n";
import { renderMarkdown } from "../../shared/lib/markdown";
import { IdentityLine } from "../../shared/components/IdentityLine";
import { HitContents, HitMetaChips } from "./HitBits";
import { HitCover, HitFilmstrip } from "./ResultMedia";
import { identityNameOf, identityUuidOf, type SearchHit } from "./searchHits";

export function PreviewPane({
  hit,
  onOpen,
  onClose,
  identity,
  openLabel,
}: {
  hit: SearchHit | null;
  onOpen: () => void;
  onClose: () => void;
  /** Override the identity line (the item list shows the item, not its host). */
  identity?: { uuid: string; name: string };
  /** Override the primary action label ("Go to item" in the item list). */
  openLabel?: string;
}) {
  const t = useT();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  const isItem = hit?.result.type === "item";
  const vocabulary = isItem ? ITEM_DEADLINE_VOCABULARY : CONTAINER_DEADLINE_VOCABULARY;
  const actionLabel =
    openLabel ?? (isItem && hit?.noPlacement ? t("goToItem") : t("goToContainer"));
  const identityValue =
    hit === null
      ? null
      : (identity ?? { uuid: identityUuidOf(hit), name: identityNameOf(hit) });
  const showIdentity = identityValue !== null && identityValue.uuid !== "";

  return (
    <Drawer
      anchor={wide ? "right" : "bottom"}
      open={hit !== null}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: wide ? 400 : "100%",
            maxHeight: wide ? "100%" : "82dvh",
            borderTopLeftRadius: { xs: 8, md: 0 },
            borderTopRightRadius: { xs: 8, md: 0 },
          },
        },
      }}
    >
      <Box sx={{ p: 1.5, overflowY: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography component="h2" variant="subtitle1" sx={{ flexGrow: 1 }}>
            {t("preview")}
          </Typography>
          <IconButton aria-label={t("close")} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {hit && (
          <Stack spacing={1.25}>
            <HitCover photo={hit.photos[0]} />
            <Box>
              <Typography variant="h6">{hit.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {hit.noPlacement ? t("searchUnplaced") : hit.trail}
              </Typography>
            </Box>
            {showIdentity && identityValue && (
              <IdentityLine uuid={identityValue.uuid} name={identityValue.name} />
            )}
            {hit.description !== "" && (
              <Box
                data-testid="preview-description"
                sx={{
                  typography: "body2",
                  color: "text.secondary",
                  "& p": { my: 0.5 },
                  "& ul": { my: 0.5, pl: 2.5 },
                  "& blockquote": {
                    my: 0.5,
                    ml: 0,
                    pl: 1.5,
                    borderLeft: 2,
                    borderColor: "divider",
                  },
                }}
              >
                {renderMarkdown(hit.description)}
              </Box>
            )}
            <HitMetaChips hit={hit} />
            <DeadlineChips properties={hit.properties} vocabulary={vocabulary} />
            <HitFilmstrip photos={hit.photos} />
            <HitContents contents={hit.contents} limit={8} />
            <Button variant="contained" onClick={onOpen}>
              {actionLabel}
            </Button>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
